import { FC, useMemo, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Button } from "@/components/elements/Button";
import { CompanyFooterContainer } from "@/features/company/components/CompanyFooterContainer";
import { CompanyUserOrgInfo } from "@/features/company/components/company-user/CompanyUserOrgInfo";
import { CompanyUserFormInfo } from "@/features/company/components/company-user/CompanyUserFormInfo";
import { useGetCompanyUserSchema } from "@/features/company/hooks/useGetCompanyUserSchema";
import { TCompanyUserForm } from "@/features/company/types/CompanyUserForm";
import { TGetCompanyDto } from "@/api/services/main/company/dtos/get-company.dto";
import { TCompanyUser } from "@/api/entities/company-user.entity";
import { deleteCompanyUserApi, resendCompanyInvitationApi, resendResetPasswordApi, updateCompanyUserApi } from "@/api/services/main/company-user";
import { getDynamicRoute } from "@/utils/url";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { IconDelete, IconResend, IconSave } from "@/assets/icons";
import { text } from "@/theme/colors";
import { useDialog } from "@/hooks/useDialog";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { CompanyUserPermissions } from "@/features/company/components/company-user/CompanyUserPermissions";
import { mapPermissionsToForm, pickSubmittablePermissions } from "@/features/company/utiles/permission";

type TCompanyUserEditContentProps = {
  companyData: TGetCompanyDto;
  userData: TCompanyUser;
};

export const CompanyUserEditContent: FC<TCompanyUserEditContentProps> = ({ companyData, userData }) => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const queryClient = useQueryClient();
  const { companyUserSchema } = useGetCompanyUserSchema();
  const [isDisableButton, setIsDisableButton] = useState(false);
  const [isReinviting, setIsReinviting] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteUserModal = useDialog();

  const initValue = useMemo(() => ({
    fullName: userData.fullName,
    email: userData.email,
    activeStatus: userData.activeStatus,
    permissions: mapPermissionsToForm(userData.permissions),
  }), [userData.fullName, userData.email, userData.activeStatus, userData.permissions]);


  const methods = useForm<TCompanyUserForm>({
    defaultValues: initValue,
    resolver: yupResolver(companyUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const onSubmit = async (values: TCompanyUserForm) => {
    if (!companyId) return;
    setIsDisableButton(true);
    try {
      const rs = await updateCompanyUserApi(companyId, userData.id, {
        ...values,
        permissions: pickSubmittablePermissions(values.permissions),
      });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_LIST, companyId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_DETAIL, companyId, String(userData.id)] });
      navigate(getDynamicRoute(AppRoutes.companyDetail, { companyId }));
    } catch (e) {
      showError(e);
    } finally {
      setIsDisableButton(false);
    }
  };

  const handleReinvite = async () => {
    if (!companyId || !userData.id) return;
    try {
      setIsReinviting(true);
      const rs = await resendCompanyInvitationApi(companyId, String(userData.id));
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_DETAIL, companyId, String(userData.id)] });
    } catch (error) {
      showError(error);
    } finally {
      setIsReinviting(false);
    }
  };

  const handleDelete = async () => {
    if (!companyId || !userData.id) return;
    const ok = await deleteUserModal.show();
    if (!ok) return;
    try {
      setIsDeleting(true);
      const rs = await deleteCompanyUserApi(companyId, userData.id);
      toast.success(rs?.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_LIST, companyId] });
      navigate(getDynamicRoute(AppRoutes.companyDetail, { companyId }));
    } catch (error) {
      showError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!companyId || !userData.id) return;
    try {
      setIsResettingPassword(true);
      const rs = await resendResetPasswordApi(companyId, String(userData.id));
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
    } catch (error) {
      showError(error);
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Stack gap={1}>
          <Typography variant="head24Bold">{companyData.companyName}のユーザー編集</Typography>
        </Stack>
        <Button
          variant="danger"
          size="sm"
          onClick={handleDelete}
          disabled={isDeleting}
          startIcon={<IconDelete />}
          sx={{
            "& .MuiButton-startIcon svg path": {
              fill: "currentColor",
            },
          }}
        >
          削除
        </Button>
      </Stack>
      <Stack mt={3}>
        <Form methods={methods} onSubmit={onSubmit} schema={companyUserSchema} initialValues={initValue}>
          <MenuNavigate
            contents={[
              { title: "基本情報", content: <CompanyUserOrgInfo companyData={companyData} /> },
              {
                title: "ユーザー情報",
                content: (
                  <CompanyUserFormInfo
                    userData={userData}
                    onPasswordReset={handleResetPassword}
                    isPasswordResetLoading={isResettingPassword}
                  />
                ),
              },
              { title: "アクセス権限", content: <CompanyUserPermissions /> },
            ]}
            contentWidth="1000px"
            contentAffix={
              <CompanyFooterContainer>
                <Stack direction="row" gap={3}>
                  <Button
                    disabled={userData.cognitoStatus !== 0 || isReinviting}
                    size="sm"
                    variant="outline"
                    onClick={handleReinvite}
                    startIcon={<IconResend />}
                    sx={{
                      "& .MuiButton-startIcon svg path": {
                        fill: "currentColor",
                      },
                    }}
                  >
                    再招待
                  </Button>
                  <Button disabled={isDisableButton} size="sm" type="submit" startIcon={<IconSave />}>
                    保存
                  </Button>
                </Stack>
              </CompanyFooterContainer>
            }
          />
        </Form>
      </Stack>
      <ConfirmDeleteModal
        open={deleteUserModal.open}
        title=""
        message="このユーザーを削除してもよろしいですか？"
        onClose={deleteUserModal.cancel}
        onOk={deleteUserModal.confirm}
      />
    </>
  );
};
